import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';

import { YearbookParams } from '../..';
import { secondaryMarket as crSecondaryMarket } from '../../data/cr';
import { secondaryMarket as fidcSecondaryMarket } from '../../data/fidc';
import { secondaryMarket as fiiSecondaryMarket } from '../../data/fii';
import { getMarketData, MarketKey } from '../../market';
import { ChartConfig, SectionMarket } from '../SectionMarket';

type DataRow = Record<string, string | number | null | undefined>;

interface SectionData {
  intro: string[];
  charts: Array<{
    title: string;
    link?: string;
    description: string;
    data: DataRow[];
  }>;
}

interface SecondaryMarketProps {
  id: string;
}

type ChartBuilder = (
  index: number,
  charts: SectionData['charts'],
) => ChartConfig;

const sectionDataByMarket: Partial<Record<MarketKey, SectionData>> = {
  crs: crSecondaryMarket as SectionData,
  fidc: fidcSecondaryMarket as SectionData,
  fii: fiiSecondaryMarket as SectionData,
};

const MONTH_NAMES = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

function monthLabel(isoDate: string): string {
  const date = new Date(isoDate);
  return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

function buildCrsChart(
  index: number,
  charts: SectionData['charts'],
): ChartConfig {
  const chart = charts[index];
  const rows = chart.data;

  return {
    type: 'line-column',
    series: [
      {
        name: 'Montante Negociado',
        type: 'column',
        data: rows.map((r) => Number(r.montante_negociado ?? 0)),
      },
      {
        name: 'Número de Negócios',
        type: 'line',
        data: rows.map((r) => Number(r.numero_negocios ?? 0)),
      },
    ],
    categories: rows.map((r) => String(r.ano)),
  };
}

function buildFidcChart(
  index: number,
  charts: SectionData['charts'],
): ChartConfig {
  const chart = charts[index];
  const rows = chart.data;

  if (index === 0) {
    return {
      type: 'line-column',
      series: [
        {
          name: 'Montante Negociado',
          type: 'column',
          data: rows.map((r) => r['Montante Negociado'] as number),
        },
        {
          name: 'Número de Negócios',
          type: 'line',
          data: rows.map((r) => r['Número de Negócios'] as number),
        },
      ],
      categories: rows.map((r) =>
        new Date(r.data_ref as string).getFullYear().toString(),
      ),
    };
  }

  if (index === 1) {
    return {
      type: 'line-column',
      series: [
        {
          name: 'Montante Negociado',
          type: 'column',
          data: rows.map((r) => r['Montante Negociado'] as number),
        },
        {
          name: 'Número de Negócios',
          type: 'line',
          data: rows.map((r) => {
            const value = r['Número de Negócios'];
            return value == null ? null : (value as number);
          }),
        },
      ],
      categories: rows.map((r) => monthLabel(r.data_ref as string)),
    };
  }

  return {
    type: 'column',
    stacked: false,
    monetary: true,
    series: [
      {
        name: 'Volume',
        data: rows.map((r) => r['Soma de montante'] as number),
      },
    ],
    categories: rows.map((r) => r.ambiente as string),
  };
}

function buildFiiChart(
  index: number,
  charts: SectionData['charts'],
): ChartConfig {
  const chart = charts[index];
  const rows = chart.data;

  return {
    type: 'line-column',
    series: [
      {
        name: 'Montante Negociado',
        type: 'column',
        data: rows.map((r) => Number(r.montante_negociado ?? 0)),
      },
      {
        name: 'Numero de Negocios',
        type: 'line',
        data: rows.map((r) => Number(r.numero_negocios ?? 0)),
      },
    ],
    categories: rows.map((r) => String(r.ano)),
  };
}

const chartBuilderByMarket: Partial<Record<MarketKey, ChartBuilder>> = {
  crs: buildCrsChart,
  fidc: buildFidcChart,
  fii: buildFiiChart,
};

export function SecondaryMarket({ id }: SecondaryMarketProps) {
  const { market } = useParams<YearbookParams>();
  const sectionData = getMarketData(market, sectionDataByMarket);
  const chartBuilder = getMarketData(market, chartBuilderByMarket);

  if (!sectionData || !chartBuilder) {
    return <Box component="section" id={id} data-info="no-content" />;
  }

  return (
    <SectionMarket
      id={id}
      title="Mercado Secundário"
      intro={sectionData.intro}
      charts={sectionData.charts}
      getChartConfig={(index) => chartBuilder(index, sectionData.charts)}
      gradientPosition="right"
    />
  );
}
